import { confirmDialog, reportLoadError, notify } from '../utils/notify';
import React, { useState, useEffect, useContext } from 'react';
import { contractsAPI, employeeAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import { formatDateForInput, formatDateForBackend , toLocalISODate, parseLocalDate } from '../utils/dateUtils';
import { Check, CirclePlay, CircleStop, Pencil, Plus, Trash2 } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { formatCurrency } from '../utils/formatCurrency';
import { CONTRACT_TYPE_LABELS, PAYMENT_STRUCTURE_LABELS, labelOf } from '../utils/labels';
import './Contracts.css';

const Contracts = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('contracts');
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Contract modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [editingContract, setEditingContract] = useState(null);

  // Template modals
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [contractForm, setContractForm] = useState({
    employee_id: '',
    template_id: '',
    contract_type: 'full-time',
    payment_structure: 'monthly',
    start_date: toLocalISODate(new Date()),
    end_date: '',
    probation_end_date: '',
    salary_amount: '',
    hourly_rate: '',
    currency: 'USD',
    health_insurance: false,
    vacation_days: 0,
    sick_leave_days: 0,
    overtime_eligible: true,
    benefits_description: '',
    terms_and_conditions: '',
    notice_period_days: 30,
    special_clauses: ''
  });

  const [templateForm, setTemplateForm] = useState({
    template_name: '',
    contract_type: 'full-time',
    payment_structure: 'monthly',
    default_duration_months: 4,
    default_probation_months: 3,
    default_salary_amount: '',
    default_hourly_rate: '',
    default_currency: 'USD',
    includes_health_insurance: false,
    includes_vacation_days: 0,
    includes_sick_leave_days: 0,
    includes_overtime_pay: true,
    custom_benefits: '',
    terms_and_conditions: '',
    notice_period_days: 30,
    is_active: true
  });

  const [filters, setFilters] = useState({
    employee_id: '',
    status: '',
    contract_type: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'contracts') {
        const [contractsRes, templatesRes, empsRes] = await Promise.all([
          contractsAPI.getAll(filters),
          contractsAPI.getTemplates(),
          employeeAPI.getAll()
        ]);
        
        const formattedContracts = contractsRes.data.contracts.map(c => ({
          ...c,
          start_date: formatDateForInput(c.start_date),
          end_date: c.end_date ? formatDateForInput(c.end_date) : ''
        }));

        setContracts(formattedContracts);
        setTemplates(templatesRes.data.templates);
        setEmployees(empsRes.data.employees.filter(e => e.status === 'active'));
      } else {
        const templatesRes = await contractsAPI.getTemplates();
        setTemplates(templatesRes.data.templates);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      reportLoadError(error, activeTab === 'contracts' ? t('Error loading contracts') : t('Error loading contract templates'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  // ========== CONTRACT HANDLERS ==========

 
  const handleEmployeeSelect = async (employeeId) => {
    if (!employeeId) {
      // Reset if no employee selected
      setContractForm({
        ...contractForm,
        employee_id: '',
        payment_structure: 'monthly',
        salary_amount: '',
        hourly_rate: '',
        currency: 'USD',
        overtime_eligible: true
      });
      return;
    }
    
    try {
      console.log('🔍 Loading employee data for ID:', employeeId);
      
      const response = await contractsAPI.getEmployeeForContract(employeeId);
      const emp = response.data.employee;
      
      console.log('📊 Employee Full Data:', emp);
      console.table({
        payment_type: emp.payment_type,
        salary: emp.salary,
        monthly_salary: emp.monthly_salary,
        hourly_rate: emp.hourly_rate,
        currency: emp.currency,
        overtime_eligible: emp.overtime_eligible
      });
      
      // ⭐ MAPEO CORRECTO basado en los datos reales
      let paymentStructure = 'monthly'; // default
      let salaryAmount = '';
      let hourlyRate = '';
      
      // Determinar payment structure desde employee.payment_type
      if (emp.payment_type) {
        paymentStructure = emp.payment_type.toLowerCase().trim();
      }
      
      // Mapear salary según payment_type
      if (paymentStructure === 'monthly') {
        // Para monthly: usar monthly_salary, si no existe usar salary
        salaryAmount = emp.monthly_salary || emp.salary || '';
        console.log(`💰 Monthly payment: using ${emp.monthly_salary ? 'monthly_salary' : 'salary'} = ${salaryAmount}`);
        
      } else if (paymentStructure === 'hourly') {
        // Para hourly: usar hourly_rate
        hourlyRate = emp.hourly_rate || '';
        console.log(`⏱️ Hourly payment: using hourly_rate = ${hourlyRate}`);
      }
      
      const newFormData = {
        ...contractForm,
        employee_id: employeeId,
        payment_structure: paymentStructure,
        salary_amount: salaryAmount,
        hourly_rate: hourlyRate,
        currency: emp.currency || 'USD',
        overtime_eligible: emp.overtime_eligible !== false
      };
      
      console.log('✅ Form will be updated with:', {
        payment_structure: newFormData.payment_structure,
        salary_amount: newFormData.salary_amount,
        hourly_rate: newFormData.hourly_rate,
        currency: newFormData.currency,
        overtime_eligible: newFormData.overtime_eligible
      });
      
      setContractForm(newFormData);
      
      // Show success message in console
      if (paymentStructure === 'monthly' && salaryAmount) {
        console.log(`✅ Autocompleted: Monthly salary ${emp.currency} ${salaryAmount}`);
      } else if (paymentStructure === 'hourly' && hourlyRate) {
        console.log(`✅ Autocompleted: Hourly rate ${emp.currency} ${hourlyRate}/hr`);
      } else {
        console.warn('⚠️ No salary data found for employee');
      }
      
    } catch (error) {
      console.error('❌ Error loading employee data:', error);
      notify(t('Error loading employee data. Please enter salary manually.'), 'error');
    }
  };
  const handleTemplateSelect = (templateId) => {
    if (!templateId) return;
    
    const template = templates.find(t => t.id === parseInt(templateId));
    if (!template) return;

    // Calculate dates. start_date is "YYYY-MM-DD"; new Date("YYYY-MM-DD") is
    // midnight UTC, i.e. 21:00 the day before in Suriname, and toLocalISODate
    // below then gave an end date one day early. Build it in local time.
    const [startY, startM, startD] = String(contractForm.start_date).slice(0, 10).split('-').map(Number);
    const startDate = new Date(startY, startM - 1, startD);
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + template.default_duration_months);
    
    const probationDate = new Date(startDate);
    probationDate.setMonth(probationDate.getMonth() + template.default_probation_months);

    setContractForm({
      ...contractForm,
      template_id: templateId,
      contract_type: template.contract_type,
      payment_structure: template.payment_structure,
      end_date: toLocalISODate(endDate),
      probation_end_date: template.default_probation_months > 0 
        ? toLocalISODate(probationDate) 
        : '',
      health_insurance: template.includes_health_insurance,
      vacation_days: template.includes_vacation_days,
      sick_leave_days: template.includes_sick_leave_days,
      overtime_eligible: template.includes_overtime_pay,
      terms_and_conditions: template.terms_and_conditions || '',
      notice_period_days: template.notice_period_days,
      // Only set salary defaults if not already set from employee
      salary_amount: contractForm.salary_amount || template.default_salary_amount || '',
      hourly_rate: contractForm.hourly_rate || template.default_hourly_rate || ''
    });
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        ...contractForm,
        start_date: formatDateForBackend(contractForm.start_date),
        end_date: contractForm.end_date ? formatDateForBackend(contractForm.end_date) : null,
        probation_end_date: contractForm.probation_end_date ? formatDateForBackend(contractForm.probation_end_date) : null
      };
      
      await contractsAPI.create(dataToSend);
      notify(t('Contract created successfully!'), 'success');
      setShowCreateModal(false);
      resetContractForm();
      await loadData();
      
    } catch (error) {
      notify(error.response?.data?.message || t('Error creating contract'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContract = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...editingContract,
        start_date: formatDateForBackend(editingContract.start_date),
        end_date: editingContract.end_date ? formatDateForBackend(editingContract.end_date) : null
      };
      
      await contractsAPI.update(editingContract.id, dataToSend);
      notify(t('Contract updated!'), 'success');
      setShowEditModal(false);
      setEditingContract(null);
      await loadData();
    } catch (error) {
      notify(t('Error updating contract'), 'error');
    }
  };

  const handleActivateContract = async (id) => {
    if (!await confirmDialog(t('Activate this contract?'))) return;
    
    try {
      await contractsAPI.activate(id);
      notify(t('Contract activated!'), 'success');
      loadData();
    } catch (error) {
      notify(error.response?.data?.message || t('Error activating contract'), 'error');
    }
  };

  const handleTerminateContract = async (id) => {
    const reason = window.prompt(t('Enter termination reason:'));
    if (!reason) return;
    
    try {
      await contractsAPI.terminate(id, { termination_reason: reason });
      notify(t('Contract terminated'), 'success');
      loadData();
    } catch (error) {
      notify(t('Error terminating contract'), 'error');
    }
  };

  const handleDeleteContract = async (id) => {
    if (!await confirmDialog(t('Delete this contract? Only drafts can be deleted.'))) return;
    
    try {
      await contractsAPI.delete(id);
      notify(t('Contract deleted'), 'success');
      loadData();
    } catch (error) {
      notify(error.response?.data?.message || t('Error deleting contract'), 'error');
    }
  };

  const resetContractForm = () => {
    setContractForm({
      employee_id: '',
      template_id: '',
      contract_type: 'full-time',
      payment_structure: 'monthly',
      start_date: toLocalISODate(new Date()),
      end_date: '',
      probation_end_date: '',
      salary_amount: '',
      hourly_rate: '',
      currency: 'USD',
      health_insurance: false,
      vacation_days: 0,
      sick_leave_days: 0,
      overtime_eligible: true,
      benefits_description: '',
      terms_and_conditions: '',
      notice_period_days: 30,
      special_clauses: ''
    });
  };

  const openEditModal = (contract) => {
    setEditingContract({
      ...contract,
      start_date: formatDateForInput(contract.start_date),
      end_date: contract.end_date ? formatDateForInput(contract.end_date) : ''
    });
    setShowEditModal(true);
  };

  // ========== TEMPLATE HANDLERS ==========

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await contractsAPI.createTemplate(templateForm);
      notify(t('Template created successfully!'), 'success');
      setShowCreateTemplateModal(false);
      resetTemplateForm();
      await loadData();
    } catch (error) {
      notify(error.response?.data?.message || t('Error creating template'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    try {
      await contractsAPI.updateTemplate(editingTemplate.id, editingTemplate);
      notify(t('Template updated!'), 'success');
      setShowEditTemplateModal(false);
      setEditingTemplate(null);
      await loadData();
    } catch (error) {
      notify(t('Error updating template'), 'error');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!await confirmDialog(t('Delete this template? This cannot be undone.'))) return;
    
    try {
      await contractsAPI.deleteTemplate(id);
      notify(t('Template deleted'), 'success');
      loadData();
    } catch (error) {
      notify(error.response?.data?.message || t('Error deleting template'), 'error');
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      template_name: '',
      contract_type: 'full-time',
      payment_structure: 'monthly',
      default_duration_months: 4,
      default_probation_months: 3,
      default_salary_amount: '',
      default_hourly_rate: '',
      default_currency: 'USD',
      includes_health_insurance: false,
      includes_vacation_days: 0,
      includes_sick_leave_days: 0,
      includes_overtime_pay: true,
      custom_benefits: '',
      terms_and_conditions: '',
      notice_period_days: 30,
      is_active: true
    });
  };

  const openEditTemplateModal = (template) => {
    setEditingTemplate({...template});
    setShowEditTemplateModal(true);
  };

  const canManage = ['super_admin', 'hr_manager'].includes(user?.role);

  return (
    <div className="contracts-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Contract Management')}</h1>
          <p className="page-sub">{t('Manage employment contracts and templates')}</p>
        </div>
        {canManage && (
          <button 
            onClick={() => activeTab === 'contracts' ? setShowCreateModal(true) : setShowCreateTemplateModal(true)} 
            className="btn btn--primary"
          >
            <Plus aria-hidden="true" /> {activeTab === 'contracts' ? t('New Contract') : t('New Template')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="tabbar">
        <button
          className={`tabbar__tab${activeTab === 'contracts' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('contracts')}
        >
          {t('Contracts')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'templates' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          {t('Templates')}
        </button>
      </div>

      {/* CONTRACTS TAB */}
      {activeTab === 'contracts' && (
        <div className="ctr-content">
          {/* Filters */}
          <div className="card ctr-filters">
            <div className="ctr-filter">
              <label className="field-label">{t('Employee')}</label>
              <select className="select"
                value={filters.employee_id}
                onChange={(e) => {
                  setFilters({...filters, employee_id: e.target.value});
                  setTimeout(loadData, 100);
                }}
              >
                <option value="">{t('All Employees')}</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ctr-filter">
              <label className="field-label">{t('Status')}</label>
              <select className="select"
                value={filters.status}
                onChange={(e) => {
                  setFilters({...filters, status: e.target.value});
                  setTimeout(loadData, 100);
                }}
              >
                <option value="">{t('All Status')}</option>
                <option value="draft">{t('Draft')}</option>
                <option value="active">{t('Active')}</option>
                <option value="expired">{t('Expired')}</option>
                <option value="terminated">{t('Terminated')}</option>
              </select>
            </div>

            <div className="ctr-filter">
              <label className="field-label">{t('Type')}</label>
              <select className="select"
                value={filters.contract_type}
                onChange={(e) => {
                  setFilters({...filters, contract_type: e.target.value});
                  setTimeout(loadData, 100);
                }}
              >
                <option value="">{t('All Types')}</option>
                <option value="full-time">{t('Full-Time')}</option>
                <option value="part-time">{t('Part-Time')}</option>
                <option value="contractor">{t('Contractor')}</option>
                <option value="temporary">{t('Temporary')}</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="empty">{t('Loading contracts...')}</div>
          ) : contracts.length === 0 ? (
            <div className="empty">
              <p>{t('No contracts found')}</p>
              {canManage && (
                <button onClick={() => setShowCreateModal(true)} className="btn btn--primary">
                  {t('Create First Contract')}
                </button>
              )}
            </div>
          ) : (
            <div className="ctr-grid">
              {contracts.map(contract => (
                <div key={contract.id} className="card ctr-card">
                  <div className="ctr-card__head">
                    <div>
                      <h3>{contract.employee_name}</h3>
                      <p className="ctr-card__number">{contract.contract_number}</p>
                    </div>
                    <StatusBadge status={contract.status}>
                      {t(contract.status)}
                    </StatusBadge>
                  </div>

                  <div className="ctr-card__info">
                    <div className="ctr-row">
                      <span className="ctr-row__label">{t('Position:')}</span>
                      <span className="ctr-row__value">{contract.position}</span>
                    </div>
                    <div className="ctr-row">
                      <span className="ctr-row__label">{t('Type:')}</span>
                      <span className="ctr-row__value">{t(labelOf(CONTRACT_TYPE_LABELS, contract.contract_type))}</span>
                    </div>
                    <div className="ctr-row">
                      <span className="ctr-row__label">{t('Payment:')}</span>
                      <span className="ctr-row__value">{t(labelOf(PAYMENT_STRUCTURE_LABELS, contract.payment_structure))}</span>
                    </div>
                    {contract.salary_amount && (
                      <div className="ctr-row">
                        <span className="ctr-row__label">{t('Salary:')}</span>
                        <span className="ctr-row__value">
                          {formatCurrency(contract.salary_amount, contract.currency || 'SRD', locale)}
                        </span>
                      </div>
                    )}
                    {contract.hourly_rate && (
                      <div className="ctr-row">
                        <span className="ctr-row__label">{t('Hourly Rate:')}</span>
                        <span className="ctr-row__value">
                          {t('{amount}/hr', { amount: formatCurrency(contract.hourly_rate, contract.currency || 'SRD', locale) })}
                        </span>
                      </div>
                    )}
                    <div className="ctr-row">
                      <span className="ctr-row__label">{t('Start Date:')}</span>
                      <span className="ctr-row__value">
                        {parseLocalDate(contract.start_date)?.toLocaleDateString(locale)}
                      </span>
                    </div>
                    {contract.end_date && (
                      <div className="ctr-row">
                        <span className="ctr-row__label">{t('End Date:')}</span>
                        <span className="ctr-row__value">
                          {parseLocalDate(contract.end_date)?.toLocaleDateString(locale)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="ctr-card__actions">
                    {canManage && (
                      <>
                        {contract.status === 'draft' && (
                          <>
                            <button
                              onClick={() => openEditModal(contract)}
                              className="btn btn--secondary"
                            >
                              <Pencil aria-hidden="true" /> {t('Edit')}
                            </button>
                            <button
                              onClick={() => handleActivateContract(contract.id)}
                              className="btn btn--primary"
                            >
                              <CirclePlay aria-hidden="true" /> {t('Activate')}
                            </button>
                            <button
                              onClick={() => handleDeleteContract(contract.id)}
                              className="btn btn--secondary ctr-danger"
                            >
                              <Trash2 aria-hidden="true" /> {t('Delete')}
                            </button>
                          </>
                        )}
                        {contract.status === 'active' && (
                          <button
                            onClick={() => handleTerminateContract(contract.id)}
                            className="btn btn--secondary ctr-danger"
                          >
                            <CircleStop aria-hidden="true" /> {t('Terminate')}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TEMPLATES TAB */}
      {activeTab === 'templates' && (
        <div className="ctr-content">
          {loading ? (
            <div className="empty">{t('Loading templates...')}</div>
          ) : (
            <div className="ctr-grid">
              {templates.map(template => (
                <div key={template.id} className="card ctr-card">
                  <div className="ctr-card__head">
                    <h3>{template.template_name}</h3>
                    <StatusBadge status={template.is_active ? 'active' : 'inactive'}>
                      {template.is_active ? t('Active') : t('Inactive')}
                    </StatusBadge>
                  </div>
                  
                  <div className="ctr-card__info">
                    <div className="ctr-row">
                      <span className="ctr-row__label">{t('Type:')}</span>
                      <span className="ctr-row__value">{t(labelOf(CONTRACT_TYPE_LABELS, template.contract_type))}</span>
                    </div>
                    <div className="ctr-row">
                      <span className="ctr-row__label">{t('Payment:')}</span>
                      <span className="ctr-row__value">{t(labelOf(PAYMENT_STRUCTURE_LABELS, template.payment_structure))}</span>
                    </div>
                    <div className="ctr-row">
                      <span className="ctr-row__label">{t('Duration:')}</span>
                      <span className="ctr-row__value">{t('{count} months', { count: template.default_duration_months })}</span>
                    </div>
                    <div className="ctr-row">
                      <span className="ctr-row__label">{t('Probation:')}</span>
                      <span className="ctr-row__value">{t('{count} months', { count: template.default_probation_months })}</span>
                    </div>
                  </div>

                  <div className="ctr-card__benefits">
                    <h4>{t('Benefits:')}</h4>
                    <ul>
                      {template.includes_health_insurance && <li><Check aria-hidden="true" /> {t('Health Insurance')}</li>}
                      {template.includes_vacation_days > 0 && (
                        <li><Check aria-hidden="true" /> {t('{count} Vacation Days', { count: template.includes_vacation_days })}</li>
                      )}
                      {template.includes_sick_leave_days > 0 && (
                        <li><Check aria-hidden="true" /> {t('{count} Sick Leave Days', { count: template.includes_sick_leave_days })}</li>
                      )}
                      {template.includes_overtime_pay && <li><Check aria-hidden="true" /> {t('Overtime Pay')}</li>}
                    </ul>
                  </div>

                  {template.terms_and_conditions && (
                    <div className="ctr-card__terms">
                      <h4>{t('Terms:')}</h4>
                      <p>{template.terms_and_conditions.substring(0, 100)}...</p>
                    </div>
                  )}

                  <div className="ctr-card__meta">
                    <small>{t('Notice Period: {days} days', { days: template.notice_period_days })}</small>
                  </div>

                  {canManage && (
                    <div className="ctr-card__actions">
                      <button
                        onClick={() => openEditTemplateModal(template)}
                        className="btn btn--secondary"
                      >
                        <Pencil aria-hidden="true" /> {t('Edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="btn btn--secondary ctr-danger"
                      >
                        <Trash2 aria-hidden="true" /> {t('Delete')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CREATE CONTRACT MODAL */}
      {showCreateModal && (
        <Modal open title={t('Create New Contract')} onClose={() => { setShowCreateModal(false); resetContractForm(); }} size="lg">

            <form onSubmit={handleCreateContract} className="ctr-dialog">
              <div className="ctr-form-row">
                <div className="ctr-field">
                  <label className="field-label">{t('Employee *')}</label>
                  <select className="select"
                    value={contractForm.employee_id}
                    onChange={(e) => handleEmployeeSelect(e.target.value)}
                    required
                  >
                    <option value="">{t('Select employee')}</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} - {emp.position}
                      </option>
                    ))}
                  </select>
                  <small className="field-hint">{t('Employee data will auto-fill salary and payment info')}</small>
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Template (Optional)')}</label>
                  <select className="select"
                    value={contractForm.template_id}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                  >
                    <option value="">{t('No template (custom contract)')}</option>
                    {templates.filter(t => t.is_active).map(tmpl => (
                      <option key={tmpl.id} value={tmpl.id}>
                        {tmpl.template_name}
                      </option>
                    ))}
                  </select>
                  <small className="field-hint">{t('Template will auto-fill terms and benefits')}</small>
                </div>
              </div>
               {/* Info Box - Mostrar qué se autocompletó */}
                {contractForm.employee_id && (
                  <div className="ctr-info">
                    <h4>{t('Autocompleted from Employee:')}</h4>
                    <ul>
                      <li>
                        <strong>{t('Payment Structure:')}</strong> {t(labelOf(PAYMENT_STRUCTURE_LABELS, contractForm.payment_structure))}
                      </li>
                      {contractForm.payment_structure === 'monthly' && contractForm.salary_amount && (
                        <li>
                          <strong>{t('Monthly Salary:')}</strong> {contractForm.currency} {contractForm.salary_amount}
                        </li>
                      )}
                      {contractForm.payment_structure === 'hourly' && contractForm.hourly_rate && (
                        <li>
                          <strong>{t('Hourly Rate:')}</strong> {t('{currency} {amount}/hr', { currency: contractForm.currency, amount: contractForm.hourly_rate })}
                        </li>
                      )}
                      <li>
                        <strong>{t('Currency:')}</strong> {contractForm.currency}
                      </li>
                      <li>
                        <strong>{t('Overtime Eligible:')}</strong> {contractForm.overtime_eligible ? t('Yes') : t('No')}
                      </li>
                    </ul>
                    <p className="ctr-info__note">{t('All values can be edited before saving')}</p>
                  </div>
                )}

                {contractForm.template_id && (
                  <div className="ctr-info">
                    <h4>{t('Applied from Template:')}</h4>
                    <ul>
                      <li><strong>{t('Contract Type:')}</strong> {t(labelOf(CONTRACT_TYPE_LABELS, contractForm.contract_type))}</li>
                      <li><strong>{t('Duration:')}</strong> {contractForm.end_date ? t('Set') : t('Not set')}</li>
                      <li><strong>{t('Benefits:')}</strong> 
                        {contractForm.health_insurance && t(' Health Insurance')}
                        {contractForm.vacation_days > 0 && t(', {count} vacation days', { count: contractForm.vacation_days })}
                      </li>
                      <li><strong>{t('Terms:')}</strong> {contractForm.terms_and_conditions ? t('Loaded') : t('Not set')}</li>
                    </ul>
                  </div>
                )}

                              <div className="ctr-form-row">
                <div className="ctr-field">
                  <label className="field-label">{t('Contract Type *')}</label>
                  <select className="select"
                    value={contractForm.contract_type}
                    onChange={(e) => setContractForm({...contractForm, contract_type: e.target.value})}
                    required
                  >
                    <option value="full-time">{t('Full-Time')}</option>
                    <option value="part-time">{t('Part-Time')}</option>
                    <option value="contractor">{t('Contractor')}</option>
                    <option value="temporary">{t('Temporary')}</option>
                  </select>
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Payment Structure *')}</label>
                  <select className="select"
                    value={contractForm.payment_structure}
                    onChange={(e) => setContractForm({...contractForm, payment_structure: e.target.value})}
                    required
                  >
                    <option value="monthly">{t('Monthly')}</option>
                    <option value="hourly">{t('Hourly')}</option>
                    <option value="daily">{t('Daily')}</option>
                    <option value="project-based">{t('Project-Based')}</option>
                  </select>
                </div>
              </div>

              <div className="ctr-form-row">
                <div className="ctr-field">
                  <label className="field-label">{t('Start Date *')}</label>
                  <input className="input"
                    type="date"
                    value={contractForm.start_date}
                    onChange={(e) => setContractForm({...contractForm, start_date: e.target.value})}
                    required
                  />
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('End Date (Optional)')}</label>
                  <input className="input"
                    type="date"
                    value={contractForm.end_date}
                    onChange={(e) => setContractForm({...contractForm, end_date: e.target.value})}
                    min={contractForm.start_date}
                  />
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Probation End Date')}</label>
                  <input className="input"
                    type="date"
                    value={contractForm.probation_end_date}
                    onChange={(e) => setContractForm({...contractForm, probation_end_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="ctr-form-row">
                {contractForm.payment_structure === 'monthly' ? (
                  <div className="ctr-field">
                    <label className="field-label">{t('Monthly Salary *')}</label>
                    <input className="input"
                      type="number"
                      value={contractForm.salary_amount}
                      onChange={(e) => setContractForm({...contractForm, salary_amount: e.target.value})}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                ) : (
                  <div className="ctr-field">
                    <label className="field-label">{t('Hourly Rate *')}</label>
                    <input className="input"
                      type="number"
                      value={contractForm.hourly_rate}
                      onChange={(e) => setContractForm({...contractForm, hourly_rate: e.target.value})}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                )}

                <div className="ctr-field">
                  <label className="field-label">{t('Currency *')}</label>
                  <select className="select"
                    value={contractForm.currency}
                    onChange={(e) => setContractForm({...contractForm, currency: e.target.value})}
                    required
                  >
                    <option value="USD">USD</option>
                    <option value="SRD">SRD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              <div className="ctr-section">
                <h3>{t('Benefits')}</h3>
                <div className="ctr-form-row">
                  <div className="ctr-field ctr-check-field">
                    <label className="ctr-check">
                      <input
                        type="checkbox"
                        checked={contractForm.health_insurance}
                        onChange={(e) => setContractForm({...contractForm, health_insurance: e.target.checked})}
                      />
                      {t('Health Insurance')}
                    </label>
                  </div>

                  <div className="ctr-field">
                    <label className="field-label">{t('Vacation Days/Year')}</label>
                    <input className="input"
                      type="number"
                      value={contractForm.vacation_days}
                      onChange={(e) => setContractForm({...contractForm, vacation_days: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>

                  <div className="ctr-field">
                    <label className="field-label">{t('Sick Leave Days/Year')}</label>
                    <input className="input"
                      type="number"
                      value={contractForm.sick_leave_days}
                      onChange={(e) => setContractForm({...contractForm, sick_leave_days: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>

                  <div className="ctr-field ctr-check-field">
                    <label className="ctr-check">
                      <input
                        type="checkbox"
                        checked={contractForm.overtime_eligible}
                        onChange={(e) => setContractForm({...contractForm, overtime_eligible: e.target.checked})}
                      />
                      {t('Overtime Eligible')}
                    </label>
                  </div>
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Additional Benefits Description')}</label>
                  <textarea className="textarea"
                    value={contractForm.benefits_description}
                    onChange={(e) => setContractForm({...contractForm, benefits_description: e.target.value})}
                    rows="3"
                    placeholder={t('e.g., Transportation allowance, meal vouchers, etc.')}
                  />
                </div>
              </div>

              <div className="ctr-field">
                <label className="field-label">{t('Terms and Conditions')}</label>
                <textarea className="textarea"
                  value={contractForm.terms_and_conditions}
                  onChange={(e) => setContractForm({...contractForm, terms_and_conditions: e.target.value})}
                  rows="4"
                  placeholder={t('Enter general terms and conditions...')}
                />
              </div>

              <div className="ctr-form-row">
                <div className="ctr-field">
                  <label className="field-label">{t('Notice Period (days)')}</label>
                  <input className="input"
                    type="number"
                    value={contractForm.notice_period_days}
                    onChange={(e) => setContractForm({...contractForm, notice_period_days: e.target.value})}
                    min="0"
                  />
                </div>
              </div>

              <div className="ctr-field">
                <label className="field-label">{t('Special Clauses')}</label>
                <textarea className="textarea"
                  value={contractForm.special_clauses}
                  onChange={(e) => setContractForm({...contractForm, special_clauses: e.target.value})}
                  rows="3"
                  placeholder={t('Enter any special clauses or conditions...')}
                />
              </div>

              <div className="ctr-form-actions">
                <button type="button" onClick={() => {
                  setShowCreateModal(false);
                  resetContractForm();
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary" disabled={loading}>
                  {loading ? t('Creating...') : t('Create Contract')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* CREATE TEMPLATE MODAL */}
      {showCreateTemplateModal && (
        <Modal open title={t('Create New Template')} onClose={() => { setShowCreateTemplateModal(false); resetTemplateForm(); }} size="lg">

            <form onSubmit={handleCreateTemplate} className="ctr-dialog">
              <div className="ctr-field">
                <label className="field-label">{t('Template Name *')}</label>
                <input className="input"
                  type="text"
                  value={templateForm.template_name}
                  onChange={(e) => setTemplateForm({...templateForm, template_name: e.target.value})}
                  required
                  placeholder={t('e.g., Full-Time Security Guard Contract')}
                />
              </div>

              <div className="ctr-form-row">
                <div className="ctr-field">
                  <label className="field-label">{t('Contract Type *')}</label>
                  <select className="select"
                    value={templateForm.contract_type}
                    onChange={(e) => setTemplateForm({...templateForm, contract_type: e.target.value})}
                    required
                  >
                    <option value="full-time">{t('Full-Time')}</option>
                    <option value="part-time">{t('Part-Time')}</option>
                    <option value="contractor">{t('Contractor')}</option>
                    <option value="temporary">{t('Temporary')}</option>
                    <option value="internship">{t('Internship')}</option>
                  </select>
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Payment Structure *')}</label>
                  <select className="select"
                    value={templateForm.payment_structure}
                    onChange={(e) => setTemplateForm({...templateForm, payment_structure: e.target.value})}
                    required
                  >
                    <option value="monthly">{t('Monthly')}</option>
                    <option value="hourly">{t('Hourly')}</option>
                    <option value="daily">{t('Daily')}</option>
                    <option value="project-based">{t('Project-Based')}</option>
                  </select>
                </div>
              </div>

              <div className="ctr-form-row">
                <div className="ctr-field">
                  <label className="field-label">{t('Default Duration (months) *')}</label>
                  <input className="input"
                    type="number"
                    value={templateForm.default_duration_months}
                    onChange={(e) => setTemplateForm({...templateForm, default_duration_months: parseInt(e.target.value)})}
                    min="1"
                    required
                  />
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Default Probation (months) *')}</label>
                  <input className="input"
                    type="number"
                    value={templateForm.default_probation_months}
                    onChange={(e) => setTemplateForm({...templateForm, default_probation_months: parseInt(e.target.value)})}
                    min="0"
                    required
                  />
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Notice Period (days) *')}</label>
                  <input className="input"
                    type="number"
                    value={templateForm.notice_period_days}
                    onChange={(e) => setTemplateForm({...templateForm, notice_period_days: parseInt(e.target.value)})}
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="ctr-section">
                <h3>{t('Default Compensation (Optional)')}</h3>
                <div className="ctr-form-row">
                  <div className="ctr-field">
                    <label className="field-label">{t('Default Salary (if monthly)')}</label>
                    <input className="input"
                      type="number"
                      value={templateForm.default_salary_amount}
                      onChange={(e) => setTemplateForm({...templateForm, default_salary_amount: e.target.value})}
                      step="0.01"
                      min="0"
                      placeholder={t("Leave empty to use employee's salary")}
                    />
                  </div>

                  <div className="ctr-field">
                    <label className="field-label">{t('Default Hourly Rate (if hourly)')}</label>
                    <input className="input"
                      type="number"
                      value={templateForm.default_hourly_rate}
                      onChange={(e) => setTemplateForm({...templateForm, default_hourly_rate: e.target.value})}
                      step="0.01"
                      min="0"
                      placeholder={t("Leave empty to use employee's rate")}
                    />
                  </div>

                  <div className="ctr-field">
                    <label className="field-label">{t('Currency')}</label>
                    <select className="select"
                      value={templateForm.default_currency}
                      onChange={(e) => setTemplateForm({...templateForm, default_currency: e.target.value})}
                    >
                      <option value="USD">USD</option>
                      <option value="SRD">SRD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="ctr-section">
                <h3>{t('Benefits')}</h3>
                <div className="ctr-form-row">
                  <div className="ctr-field ctr-check-field">
                    <label className="ctr-check">
                      <input
                        type="checkbox"
                        checked={templateForm.includes_health_insurance}
                        onChange={(e) => setTemplateForm({...templateForm, includes_health_insurance: e.target.checked})}
                      />
                      {t('Includes Health Insurance')}
                    </label>
                  </div>

                  <div className="ctr-field">
                    <label className="field-label">{t('Vacation Days/Year')}</label>
                    <input className="input"
                      type="number"
                      value={templateForm.includes_vacation_days}
                      onChange={(e) => setTemplateForm({...templateForm, includes_vacation_days: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>

                  <div className="ctr-field">
                    <label className="field-label">{t('Sick Leave Days/Year')}</label>
                    <input className="input"
                      type="number"
                      value={templateForm.includes_sick_leave_days}
                      onChange={(e) => setTemplateForm({...templateForm, includes_sick_leave_days: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>

                  <div className="ctr-field ctr-check-field">
                    <label className="ctr-check">
                      <input
                        type="checkbox"
                        checked={templateForm.includes_overtime_pay}
                        onChange={(e) => setTemplateForm({...templateForm, includes_overtime_pay: e.target.checked})}
                      />
                      {t('Includes Overtime Pay')}
                    </label>
                  </div>
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Custom Benefits')}</label>
                  <textarea className="textarea"
                    value={templateForm.custom_benefits}
                    onChange={(e) => setTemplateForm({...templateForm, custom_benefits: e.target.value})}
                    rows="2"
                    placeholder={t('e.g., Transportation allowance, meal vouchers')}
                  />
                </div>
              </div>

              <div className="ctr-field">
                <label className="field-label">{t('Terms and Conditions *')}</label>
                <textarea className="textarea"
                  value={templateForm.terms_and_conditions}
                  onChange={(e) => setTemplateForm({...templateForm, terms_and_conditions: e.target.value})}
                  rows="6"
                  required
                  placeholder={t('Enter standard terms and conditions for this type of contract...')}
                />
              </div>

              <div className="ctr-field ctr-check-field">
                <label className="ctr-check">
                  <input
                    type="checkbox"
                    checked={templateForm.is_active}
                    onChange={(e) => setTemplateForm({...templateForm, is_active: e.target.checked})}
                  />
                  {t('Active (available for use)')}
                </label>
              </div>

              <div className="ctr-form-actions">
                <button type="button" onClick={() => {
                  setShowCreateTemplateModal(false);
                  resetTemplateForm();
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary" disabled={loading}>
                  {loading ? t('Creating...') : t('Create Template')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* EDIT TEMPLATE MODAL */}
      {showEditTemplateModal && editingTemplate && (
        <Modal open title={t('Edit Template')} onClose={() => { setShowEditTemplateModal(false); setEditingTemplate(null); }} size="lg">

            <form onSubmit={handleUpdateTemplate} className="ctr-dialog">
              <div className="ctr-field">
                <label className="field-label">{t('Template Name *')}</label>
                <input className="input"
                  type="text"
                  value={editingTemplate.template_name}
                  onChange={(e) => setEditingTemplate({...editingTemplate, template_name: e.target.value})}
                  required
                />
              </div>

              <div className="ctr-form-row">
                <div className="ctr-field">
                  <label className="field-label">{t('Contract Type *')}</label>
                  <select className="select"
                    value={editingTemplate.contract_type}
                    onChange={(e) => setEditingTemplate({...editingTemplate, contract_type: e.target.value})}
                    required
                  >
                    <option value="full-time">{t('Full-Time')}</option>
                    <option value="part-time">{t('Part-Time')}</option>
                    <option value="contractor">{t('Contractor')}</option>
                    <option value="temporary">{t('Temporary')}</option>
                    <option value="internship">{t('Internship')}</option>
                  </select>
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Payment Structure *')}</label>
                  <select className="select"
                    value={editingTemplate.payment_structure}
                    onChange={(e) => setEditingTemplate({...editingTemplate, payment_structure: e.target.value})}
                    required
                  >
                    <option value="monthly">{t('Monthly')}</option>
                    <option value="hourly">{t('Hourly')}</option>
                    <option value="daily">{t('Daily')}</option>
                    <option value="project-based">{t('Project-Based')}</option>
                  </select>
                </div>
              </div>

              <div className="ctr-form-row">
                <div className="ctr-field">
                  <label className="field-label">{t('Default Duration (months) *')}</label>
                  <input className="input"
                    type="number"
                    value={editingTemplate.default_duration_months}
                    onChange={(e) => setEditingTemplate({...editingTemplate, default_duration_months: parseInt(e.target.value)})}
                    min="1"
                    required
                  />
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Default Probation (months) *')}</label>
                  <input className="input"
                    type="number"
                    value={editingTemplate.default_probation_months}
                    onChange={(e) => setEditingTemplate({...editingTemplate, default_probation_months: parseInt(e.target.value)})}
                    min="0"
                    required
                  />
                </div>

                <div className="ctr-field">
                  <label className="field-label">{t('Notice Period (days) *')}</label>
                  <input className="input"
                    type="number"
                    value={editingTemplate.notice_period_days}
                    onChange={(e) => setEditingTemplate({...editingTemplate, notice_period_days: parseInt(e.target.value)})}
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="ctr-section">
                <h3>{t('Benefits')}</h3>
                <div className="ctr-form-row">
                  <div className="ctr-field ctr-check-field">
                    <label className="ctr-check">
                      <input
                        type="checkbox"
                        checked={editingTemplate.includes_health_insurance}
                        onChange={(e) => setEditingTemplate({...editingTemplate, includes_health_insurance: e.target.checked})}
                      />
                      {t('Includes Health Insurance')}
                    </label>
                  </div>

                  <div className="ctr-field">
                    <label className="field-label">{t('Vacation Days/Year')}</label>
                    <input className="input"
                      type="number"
                      value={editingTemplate.includes_vacation_days}
                      onChange={(e) => setEditingTemplate({...editingTemplate, includes_vacation_days: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>

                  <div className="ctr-field">
                    <label className="field-label">{t('Sick Leave Days/Year')}</label>
                    <input className="input"
                      type="number"
                      value={editingTemplate.includes_sick_leave_days}
                      onChange={(e) => setEditingTemplate({...editingTemplate, includes_sick_leave_days: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>

                  <div className="ctr-field ctr-check-field">
                    <label className="ctr-check">
                      <input
                        type="checkbox"
                        checked={editingTemplate.includes_overtime_pay}
                        onChange={(e) => setEditingTemplate({...editingTemplate, includes_overtime_pay: e.target.checked})}
                      />
                      {t('Includes Overtime Pay')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="ctr-field">
                <label className="field-label">{t('Terms and Conditions *')}</label>
                <textarea className="textarea"
                  value={editingTemplate.terms_and_conditions}
                  onChange={(e) => setEditingTemplate({...editingTemplate, terms_and_conditions: e.target.value})}
                  rows="6"
                  required
                />
              </div>

              <div className="ctr-field ctr-check-field">
                <label className="ctr-check">
                  <input
                    type="checkbox"
                    checked={editingTemplate.is_active}
                    onChange={(e) => setEditingTemplate({...editingTemplate, is_active: e.target.checked})}
                  />
                  {t('Active (available for use)')}
                </label>
              </div>

              <div className="ctr-form-actions">
                <button type="button" onClick={() => {
                  setShowEditTemplateModal(false);
                  setEditingTemplate(null);
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Update Template')}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default Contracts;